import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs'

import { HeaderStyle } from './types'

const [showSearchChange$, setShowSearch] = createSignal<boolean>()
export const showSearch$ = showSearchChange$.pipe(startWith(false), distinctUntilChanged())

export { setShowSearch }
export const [useShowSearch] = bind(showSearch$, false)

const [activeItemChange$, setActiveItem] = createSignal<string | null | undefined>()
export const activeItem$ = activeItemChange$.pipe(startWith(null), distinctUntilChanged())

export const [useActiveItem] = bind(activeItem$, null)
export { setActiveItem }

const [isMobileMenuOpenChange$, setIsMobileMenuOpen] = createSignal<boolean>()
const isMobileMenuOpen$ = isMobileMenuOpenChange$.pipe(startWith(false), distinctUntilChanged())

export const [useIsMobileMenuOpen] = bind(isMobileMenuOpen$, false)
export { setIsMobileMenuOpen }

const [brandMenuOpenChange$, setBrandMenuOpen] = createSignal<boolean>()
const brandMenuOpen$ = brandMenuOpenChange$.pipe(startWith(false), distinctUntilChanged())

export const [useBrandMenuOpen] = bind(brandMenuOpen$, false)
export { setBrandMenuOpen }

const [brandMenuActiveItemChange$, setBrandMenuActiveItem] = createSignal<string>()
export const brandMenuActiveItem$ = brandMenuActiveItemChange$.pipe(
  startWith(''),
  distinctUntilChanged(),
)

export const [useBrandMenuActiveItem] = bind(brandMenuActiveItem$, '')
export { setBrandMenuActiveItem }

const mediaQueryFactory = (query: string) =>
  new Observable<boolean>((subscriber) => {
    const mediaQueryList = window.matchMedia(query)
    const onChange = () => subscriber.next(mediaQueryList.matches)
    mediaQueryList.addListener(onChange)
    subscriber.next(mediaQueryList.matches)
    return () => mediaQueryList.removeListener(onChange)
  })

const isDesktop$ = mediaQueryFactory('(min-width: 1024px)')
const isTablet$ = mediaQueryFactory('(min-width: 768px)')

const scrollDirection$ = (threshold: number) =>
  new Observable<'up' | 'down'>((subscriber) => {
    let lastScrollDirection = ''
    let lastScrollY = window.scrollY
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      if (currentScrollY < threshold) {
        if (lastScrollDirection !== 'up') {
          lastScrollDirection = 'up'
          subscriber.next('up')
        }
      } else if (currentScrollY > lastScrollY && lastScrollDirection !== 'down') {
        lastScrollDirection = 'down'
        subscriber.next('down')
      } else if (currentScrollY < lastScrollY && lastScrollDirection !== 'up') {
        lastScrollDirection = 'up'
        subscriber.next('up')
      }
      lastScrollY = currentScrollY
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  })

export const hideHeader$ = combineLatest([
  showSearch$,
  activeItem$,
  isMobileMenuOpen$,
  brandMenuOpen$,
  isDesktop$,
  isTablet$,
]).pipe(
  switchMap(([showSearch, activeItem, isMobileMenuOpen, brandMenuOpen, isDesktop, isTablet]) => {
    const threshold = isDesktop ? 101 : isTablet ? 80 : 60
    return scrollDirection$(threshold).pipe(
      map((scrollDirection) =>
        showSearch || scrollDirection !== 'down'
          ? false
          : isDesktop || isTablet
            ? !activeItem
            : !isMobileMenuOpen && !brandMenuOpen,
      ),
    )
  }),
  distinctUntilChanged(),
  tap((hideHeader) => {
    if (hideHeader) {
      document.body.style.setProperty('--hiding-header-translate-y', '-100%')
    } else {
      document.body.style.removeProperty('--hiding-header-translate-y')
    }
  }),
)

export const [headerHovered$, setHeaderHovered] = createSignal<boolean>()

const headerScrollOffset = 10
const isHeaderScrolled = () => {
  if (typeof window === 'undefined') return false
  return window.scrollY >= headerScrollOffset
}
export const isHeaderScrolled$ =
  typeof window === 'undefined'
    ? of(false)
    : fromEvent(window, 'scroll').pipe(
        map(isHeaderScrolled),
        startWith(isHeaderScrolled()),
        distinctUntilChanged(),
      )

const headerStyleBaseSubject = new BehaviorSubject<HeaderStyle>('white')

export const headerStyleBase$ = headerStyleBaseSubject.asObservable()
export const setHeaderStyleBase = (style: HeaderStyle) => headerStyleBaseSubject.next(style)
